import { useEffect } from "react";
import { useLocation, useMatches } from "react-router-dom";
import useStore from "@/hooks/useStore";

// WordPress post type mapping
const POST_TYPES = {
  "case-studies": "case-studies",
  "open-roles": "open-roles",
  blog_authors: "blog_authors",

  // taxonomies
  category: "categories",
  tag: "tags",

  // blog
  insights: "posts",
};

/**
 * Determine the WordPress post type based on pathname.
 * Unified logic for:
 *  - HOME
 *  - PAGES
 *  - CPTs (insights, case-studies, open-roles, blog_authors)
 *  - Taxonomies
 *  - Invalid legacy URL patterns
 */
const getPostType = (pathname) => {
  const path = pathname.replace(/\/+$/, "");
  const segments = path.split("/").filter(Boolean);
  const base = segments[0] || "";
  const len = segments.length;

  // 1. HOME → page
  if (len === 0) return "pages";

  // 2. TAXONOMIES (archives)
  if (base === "category" || base === "tag") {
    return POST_TYPES[base]; // categories or tags
  }

  // 3. CPTs (general rule for all custom post types)
  const isCPT = POST_TYPES[base] && base !== "category" && base !== "tag";

  if (isCPT) {
    if (len === 1) {
      return "pages"; // /cpt → archive
    }
    if (len >= 2) {
      return POST_TYPES[base]; // /cpt/slug or /cpt/cat/slug
    }
  }

  // 4. PAGES → any single-segment slug (e.g. /about)
  if (len === 1) {
    return "pages";
  }

  // 5. INVALID legacy URL (WP will redirect)
  return null; // TitleManager will ignore these
};

const TitleManager = () => {
  const { pathname, search } = useLocation();
  const matches = useMatches();
  const { getSingleBySlug, updateDocTitle } = useStore();
  const isPreview = window.__NS_PREVIEW__ && new URLSearchParams(search).get("preview") === "true";
  const previewTitle = matches.at(-1)?.data?.previewTitle;

  useEffect(() => {
    let active = true;
    const siteName = window.__REACT_WORDPRESS__.state.site.name;

    if (isPreview) {
      // Loaders resolve before the route commits. Reuse the merged autosave
      // title, not the saved parent's SEO title or the homepage pathname.
      const decoded = document.createElement("textarea");
      decoded.innerHTML = previewTitle ?? "";
      updateDocTitle({ title: decoded.value ? `${decoded.value} – ${siteName}` : siteName });
      return;
    }

    const segments = pathname.split("/").filter(Boolean);
    const slug = segments.at(-1) || "home-v2";
    const postType = getPostType(pathname);
    if (postType === null) return;

    getSingleBySlug({ slug, type: postType })
      .then((res) => {
        if (active) updateDocTitle({ title: res?.seo?.rank_math_title || siteName });
      })
      .catch(() => {
        if (active) updateDocTitle({ title: siteName });
      });

    // A request started on the previous route must not overwrite this title.
    return () => {
      active = false;
    };
  }, [pathname, search, isPreview, previewTitle]);

  return null;
};

export default TitleManager;
